/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { HTMLAttributes, ReactNode } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import { radius, spacing } from "../../../../../global/emotion";
import { Text } from "../Text/Text.emotion";
import { Avatar } from "./Avatar.emotion";

export type SocialCountAvatar = {
  id: string | number;
  imgSrc?: string;
  name?: string;
};

export type SocialCountOptions = {
  maxVisible?: number;
  borderColor?: string;
};

export type SocialCountProps = HTMLAttributes<HTMLElement> & {
  avatars: SocialCountAvatar[];
  totalCount: number;
  label?: ReactNode;
  options?: SocialCountOptions;
};

const AVATAR_SIZE = 24;
const DEFAULT_MAX_VISIBLE = 3;

const containerCss: Interpolation = {
  display: "flex",
  alignItems: "center",
  gap: spacing.s,
};

const buttonResetCss: Interpolation = {
  appearance: "none",
  background: "none",
  border: "none",
  padding: 0,
  margin: 0,
  cursor: "pointer",
  textAlign: "left",
  color: "inherit",
  font: "inherit",
};

const stackCss: Interpolation = {
  display: "flex",
  alignItems: "center",
};

const avatarWrapperCss =
  (borderColor?: string): Interpolation<ThemeType> =>
  (theme) => ({
    position: "relative",
    borderRadius: radius.full,
    boxShadow: `0 0 0 2px ${borderColor ?? theme.semanticColors.background}`,
    marginLeft: -spacing.xxs,
    "&:first-of-type": { marginLeft: 0 },
  });

const overflowCss =
  (borderColor?: string): Interpolation<ThemeType> =>
  (theme) => ({
    height: AVATAR_SIZE,
    minWidth: AVATAR_SIZE,
    borderRadius: radius.full,
    backgroundColor: theme.semanticColors.neutralsLowest,
    boxShadow: `0 0 0 2px ${borderColor ?? theme.semanticColors.background}`,
    color: theme.semanticColors.primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: `4px ${spacing.xs}`,
    marginLeft: -spacing.xxs,
  });

const labelCss: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
  whiteSpace: "nowrap",
});

export const SocialCount = ({
  avatars,
  totalCount,
  label,
  options,
  onClick,
  ...props
}: SocialCountProps) => {
  const { maxVisible = DEFAULT_MAX_VISIBLE, borderColor } = options ?? {};
  const visibleAvatars = avatars.slice(0, maxVisible);
  const overflow = Math.max(0, totalCount - visibleAvatars.length);

  const content = (
    <>
      <div css={stackCss}>
        {visibleAvatars.map((avatar) => (
          <div key={avatar.id} css={avatarWrapperCss(borderColor)}>
            <Avatar
              size={AVATAR_SIZE}
              imgSrc={avatar.imgSrc ?? ""}
              name={avatar.name ?? ""}
            />
          </div>
        ))}
        {overflow > 0 && (
          <div css={overflowCss(borderColor)}>
            <Text type="button" size="xs">
              +{overflow}
            </Text>
          </div>
        )}
      </div>
      {label && (
        <Text type="detail" size="xl" css={labelCss}>
          {label}
        </Text>
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        css={[buttonResetCss, containerCss]}
        {...props}
      >
        {content}
      </button>
    );
  }

  return (
    <div css={containerCss} {...props}>
      {content}
    </div>
  );
};

SocialCount.displayName = "SocialCount";
